import {IRecordPhysicalActivity} from './record-physical-activity.interface';
import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {BaseService, DataBaseServiceResponse} from '../../services/base-service/base.service';
import {environment} from '../../../environments/environment';
import {firstValueFrom} from 'rxjs';

export interface IRRecordPhysicalActivity {
  recordPhysicalActivity: IRecordPhysicalActivity;
  wasDeployed?: boolean;
}

export namespace RecordPhysicalActivityAction {
  export class GetActive {
    static readonly type = '[Record] Get RecordPhysicalActivity Action';
    constructor(
      public serviceId: string,
      public patientId: string
    ) { }
  }

  export class Post {
    static readonly type = '[Record] Post RecordPhysicalActivity Action';
    constructor(
      public serviceId: string,
      public patientId: string,
      public recordPhysicalActivity: Partial<IRecordPhysicalActivity>
    ) { }
  }

  export class PatchActive {
    static readonly type = '[Record] Patch RecordPhysicalActivity Action';
    constructor(
      public serviceId: string,
      public patientId: string,
      public physicalActivityId: string,
      public recordPhysicalActivity: Partial<IRecordPhysicalActivity>
    ) { }
  }

  export class ResetState {
    static readonly type = '[Record] Reset RecordPhysicalActivity Action';
    constructor() { }
  }

  export class Deploy {
    static readonly type = '[Record] Was Deployed Physical Activity Action';
    constructor(public value: boolean) { }
  }
}

@State<IRRecordPhysicalActivity>({
  name: 'RecordPhysicalActivityState',
  defaults: {
    recordPhysicalActivity: null
  },
})

@Injectable()
export class RecordPhysicalActivityState {
  constructor(
    private baseService: BaseService<any>,
  ) {
  }

  url = environment.SERVER_URL;

  @Selector()
  public static getRecordPhysicalActivity(state: IRRecordPhysicalActivity): IRecordPhysicalActivity {
    return state.recordPhysicalActivity;
  }

  @Selector()
  public static getWasDeployed(state: IRRecordPhysicalActivity): boolean {
    return state.wasDeployed;
  }

  @Action(RecordPhysicalActivityAction.GetActive)
  async getRecordPhysicalActivityAction(
    ctx: StateContext<IRRecordPhysicalActivity>,
    action: RecordPhysicalActivityAction.GetActive
  ): Promise<void> {
    const response: DataBaseServiceResponse<IRecordPhysicalActivity> = await firstValueFrom(
      this.baseService.get(`${this.url}/patients/${action.patientId}/service/${action.serviceId}/records/physical-activity/active`)
    );

    if (response.error) throw new Error(response.message);

    ctx.patchState({
      recordPhysicalActivity: response.entity
    });
  }

  @Action(RecordPhysicalActivityAction.Post)
  async postRecordPhysicalActivityAction(
    ctx: StateContext<IRRecordPhysicalActivity>,
    action: RecordPhysicalActivityAction.Post
  ): Promise<void> {
    const response: DataBaseServiceResponse<IRecordPhysicalActivity> = await firstValueFrom(
      this.baseService.post(
        `${this.url}/patients/${action.patientId}/service/${action.serviceId}/records/physical-activity`,
        action.recordPhysicalActivity
      )
    );

    if (response.error) throw new Error(response.message);
  }

  @Action(RecordPhysicalActivityAction.PatchActive)
  async patchRecordPhysicalActivityAction(
    ctx: StateContext<IRRecordPhysicalActivity>,
    {recordPhysicalActivity, physicalActivityId, serviceId, patientId}: RecordPhysicalActivityAction.PatchActive
  ): Promise<void> {
    const response: DataBaseServiceResponse<void> = await firstValueFrom(
      this.baseService.patch(
        `${this.url}/patients/${patientId}/service/${serviceId}/records/physical-activity/${physicalActivityId}`,
        recordPhysicalActivity
      )
    );

    if (response.error) throw new Error(response.message);
  }

  @Action(RecordPhysicalActivityAction.ResetState)
  async resetRecordPhysicalActivityAction(
    ctx: StateContext<IRRecordPhysicalActivity>,
    _: RecordPhysicalActivityAction.ResetState
  ): Promise<void> {
    ctx.patchState({
      recordPhysicalActivity: null
    });
  }

  @Action(RecordPhysicalActivityAction.Deploy)
  async wasDeployedPhysicalActivityAction(
    ctx: StateContext<IRRecordPhysicalActivity>,
    action: RecordPhysicalActivityAction.Deploy
  ): Promise<void> {
    ctx.patchState({
      wasDeployed: action.value
    });
  }
}
